import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import Card from '../../common/cards/Card';
import CardGiftcardRoundedIcon from '@material-ui/icons/CardGiftcardRounded';
import ArrowBackIosRoundedIcon from '@material-ui/icons/ArrowBackIosRounded';
import CategoryRoundedIcon from '@material-ui/icons/CategoryRounded';
import StyledTab from '../../common/tabs/SimpleTab';
import { a11yProps, addExtraBackButtonControls } from '../../../helpers/utils';
import SimpleTabs from '../../common/tabs/SimpleTabs';
import TabPanel from '../../common/tabs/SimpleTabPanel';
import {
  addContentAction,
  addFabAction,
  closeSliderDialogAction,
  openSliderDialogAction
} from '../../../reactRedux/Actions/showSliderDialogAction';
import GiftDetailDialog from '../../_dialogContent/GiftDetailDialog';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { parseDate } from '../../../helpers/date';
import classNames from 'classnames';
import StatusProgress from '../../common/StatusProgress';
import Loader from '../../common/Loader';
import SwipeableViews from 'react-swipeable-views';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import { IconButton } from '@material-ui/core';
import Eof from '../../common/Eof';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  claimRewardRequest,
  getCategoryRewardsRequest,
  getMyRewardsRequest,
  getRewardCategoriesRequest
} from './api';
import Button from '@mui/material/Button';
import { useTranslation } from '../../../i18n/LanguageProvider';

const Gift = () => {
  const { t, repr, isRtl } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isDesktop = useDesktopDisplay();

  const [value, setValue] = React.useState(0);
  const [category, setCategory] = React.useState(null);
  const [limit] = useState(6);
  const [showDesc, setShowDesc] = React.useState([]);

  const userScore = useSelector((state) => state.userReducer.score);
  const dialogShow = useSelector((state) => state.sliderDialogReducer.show);

  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));
  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const openSliderDialog = () => dispatch(openSliderDialogAction());
  const closeDialog = () => dispatch(closeSliderDialogAction());
  const setDialogContent = (content) => dispatch(addContentAction(content));
  const setDialogFab = (fab) => dispatch(addFabAction(fab));

  const { isLoading: categoriesQueryIsLoading, data: categoriesQueryData } =
    useQuery(['rewardCategories'], getRewardCategoriesRequest, {
      onError(err) {
        toast.error(err.response?.data.detail || t('rewards.categoriesError'));
      }
    });

  const claimRewardMutation = useMutation(claimRewardRequest, {
    onSuccess() {
      toast.success(t('rewards.claimed'));
      setValue(1);
      closeDialog();
    },
    onError(err) {
      toast.error(err.message);
    }
  });

  const {
    isLoading: myRewardsIsLoading,
    data: myRewardsData,
    refetch: myRewardsRefetch
  } = useQuery(['myRewards'], getMyRewardsRequest, {
    onError(err) {
      toast.error(err.response?.data.detail || t('rewards.myRewardsError'));
    }
  });

  useEffect(() => {
    setHeader(t('nav.rewards'));
  }, []);

  useEffect(() => {
    if (!claimRewardMutation.isLoading) {
      setHeaderConfigs({
        credits: true,
        exit: false,
        back: false,
        logo: false
      });

      myRewardsRefetch();
    }
  }, [claimRewardMutation.isLoading]);

  const {
    isLoading: categoryRewardsIsLoading,
    data: categoryRewardsData,
    hasNextPage: categoryRewardsHasNextPage,
    isFetchingNextPage: categoryRewardsIsFetchingNextPage,
    fetchNextPage: categoryRewardsFetchNextPage
  } = useInfiniteQuery(
    ['categoryRewards', category],
    ({ pageParam = 1 }) =>
      getCategoryRewardsRequest({
        categoryId: category,
        limit,
        page: pageParam
      }),
    {
      enabled: !!category,
      select: React.useCallback(
        (data) => data?.pages.flatMap((giftPage) => giftPage),
        []
      ),
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length > 0 ? nextPage : undefined;
      },
      onError(err) {
        toast.error(
          err.response?.data?.detail || t('rewards.categoryRewardsError')
        );
      }
    }
  );

  const chooseCategory = (i) => {
    setCategory(i);
    window.history.pushState(null, null, window.location.pathname);
  };

  useEffect(() => {
    if (claimRewardMutation.isLoading) {
      setDialogFab([
        {
          type: 'accept',
          loading: true,
          disabled: true,
          onClickFn: null
        },
        {
          type: 'default',
          isClose: true,
          iconOnly: true,
          onClickFn: closeDialog
        }
      ]);
    }
  }, [claimRewardMutation.isLoading]);

  const setDialog = (data) => () => {
    setDialogContent(<GiftDetailDialog data={data} />);
    setDialogFab(
      userScore < data.coin
        ? [
            {
              type: 'default',
              isClose: true,
              title: t('rewards.needMore', { count: data.coin - userScore }),
              onClickFn: closeDialog
            }
          ]
        : data.stock === 0
        ? [
            {
              type: 'default',
              isClose: true,
              title: t('rewards.unavailable'),
              onClickFn: closeDialog
            }
          ]
        : [
            {
              type: 'accept',
              title: t('rewards.want'),
              onClickFn: () => claimRewardMutation.mutate({ rewardId: data.id })
            },
            {
              type: 'default',
              isClose: true,
              iconOnly: true,
              onClickFn: closeDialog
            }
          ]
    );
    openSliderDialog();
  };

  useEffect(() => {
    addExtraBackButtonControls({
      data: category,
      func: () => {
        if (!dialogShow) {
          setCategory(null);
        }
      }
    });

    return () => addExtraBackButtonControls({});
  }, [category, dialogShow]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  const showGiftDesc = (i) => () => {
    setShowDesc([
      ...showDesc.slice(0, i),
      !showDesc[i],
      ...showDesc.slice(i + 1)
    ]);
  };

  const findCategoryTitle = (id) => {
    return categoriesQueryData?.filter((c) => c.id === id)[0]?.title;
  };

  const findCategoryDescription = (id) => {
    return categoriesQueryData?.filter((c) => c.id === id)[0]?.description;
  };

  const closeCategory = () => {
    setCategory(null);
    history.goBack();
  };

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{t('rewards.pageTitle')}</title>
      </Helmet>
      {isDesktop && <div className={styles.pageHeader}>{t('nav.rewards')}</div>}
      <div className={styles.tabs}>
        <SimpleTabs value={value} handleChange={handleChange}>
          <StyledTab label={t('rewards.shop')} {...a11yProps(0)} />
          <StyledTab label={t('rewards.mine')} {...a11yProps(1)} />
        </SimpleTabs>
      </div>
      <div className={styles.container}>
        <SwipeableViews
          axis={isRtl ? 'x-reverse' : 'x'}
          index={value}
          onChangeIndex={handleChangeIndex}
          className={classNames(styles.swipeArea, styles.noMargin)}
        >
          <TabPanel index={0} value={value}>
            <div
              className={classNames(styles.categoryTitle, {
                [styles.animation]: !category
              })}
            >
              <div className={styles.categoryTopRow}>
                <h4>{findCategoryTitle(category)}</h4>
                <IconButton
                  edge={'start'}
                  className={styles.link}
                  onClick={closeCategory}
                >
                  <span>{t('rewards.categories')}</span>
                  <ArrowBackIosRoundedIcon className="flip-ltr" />
                </IconButton>
              </div>
              <span className={styles.categoryDesc}>
                {findCategoryDescription(category)}
              </span>
            </div>
            {categoriesQueryIsLoading ? (
              <Loader size={40} />
            ) : (
              <div className={styles.wrapper}>
                {!category ? (
                  categoriesQueryData?.length ? (
                    categoriesQueryData?.map((c, i) => (
                      <Card
                        key={i}
                        extraClass={styles.giftCard}
                        onClick={() => chooseCategory(c.id)}
                      >
                        <div className={styles.imageBox}>
                          {c.photo ? (
                            <img
                              src={c.photo?.thumbnail}
                              alt={c.title}
                              className={styles.thumbnail}
                            />
                          ) : (
                            <CategoryRoundedIcon className={styles.icon} />
                          )}
                        </div>
                        <div className={styles.infoBox}>
                          <div className={styles.title}>{c.title}</div>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className={styles.empty}>{t('rewards.shopEmpty')}</div>
                  )
                ) : categoryRewardsIsLoading ? (
                  <Loader size={40} />
                ) : categoryRewardsData?.length ? (
                  <>
                    {categoryRewardsData?.map((g, i) => (
                      <Card
                        key={i}
                        extraClass={styles.giftCard}
                        onClick={setDialog(g)}
                      >
                        <div className={styles.imageBox}>
                          {g.photo ? (
                            <img
                              src={g.photo.thumbnail}
                              alt={'photo of ' + g.title}
                              className={styles.thumbnail}
                            />
                          ) : (
                            <CardGiftcardRoundedIcon className={styles.icon} />
                          )}
                        </div>
                        <div className={styles.infoBox}>
                          <div className={styles.title}>{g.title}</div>
                          <div className={styles.price}>{g.coin}</div>
                        </div>
                      </Card>
                    ))}
                    {categoryRewardsHasNextPage ? (
                      categoryRewardsIsFetchingNextPage ? (
                        <Loader size={40} />
                      ) : (
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={categoryRewardsFetchNextPage}
                        >
                          {t('common.more')}
                        </Button>
                      )
                    ) : (
                      <Eof />
                    )}
                  </>
                ) : (
                  <div className={styles.empty}>
                    {t('rewards.categoryEmpty')}
                  </div>
                )}
              </div>
            )}
          </TabPanel>
          <TabPanel index={1} value={value}>
            <div className={styles.wrapper}>
              {myRewardsIsLoading ? (
                <Loader size={40} />
              ) : myRewardsData.length ? (
                myRewardsData.map((g, i) => (
                  <Card key={i} extraClass={styles.myGiftCard} noPointer>
                    <div className={styles.info}>
                      <span className={styles.title}>{g.reward.title}</span>
                      <span className={styles.date}>
                        {t('rewards.requestDate', {
                          date: parseDate(g.created_at, true)
                        })}
                      </span>
                      <button
                        className={styles.showDesc}
                        onClick={showGiftDesc(i)}
                      >
                        {showDesc[i]
                          ? t('rewards.hideDescription')
                          : t('rewards.showDescription')}
                      </button>
                      <span
                        className={classNames(styles.desc, {
                          [styles.show]: showDesc[i]
                        })}
                      >
                        {g.reward.description}
                      </span>
                    </div>
                    <div className={styles.progress}>
                      <StatusProgress
                        steps={3}
                        current={g.status_repr?.index}
                        label={repr(g.status_repr, 'rewardOrderStatus')}
                      />
                    </div>
                  </Card>
                ))
              ) : (
                <div className={styles.empty}>{t('rewards.noneYet')}</div>
              )}
            </div>
          </TabPanel>
        </SwipeableViews>
      </div>
    </div>
  );
};

Gift.propTypes = {};

export default Gift;
